import React, { useState } from 'react';
import { agreementsApi } from '@/api';
import type { Agreement, AgreementStatus } from '@/types';
import { Badge, Button } from '@/ui';

export type AgreementSnapshot = {
  id: string;
  status: AgreementStatus;
  sentAt?: string | null;
  signedAt?: string | null;
  signedPdfUrl?: string | null;
};

interface AgreementSendPanelProps {
  appointmentId: string;
  clientEmail: string | null;
  agreement: AgreementSnapshot | null;
  /**
   * Fired after a successful send so the parent can refresh its local state
   * (e.g. update the appointment row, or replace the latest agreement on
   * the inspection page).
   */
  onSent: (agreement: Agreement) => void;
  /**
   * Hide the "Send agreement" button — used when the parent renders its own
   * CTA (e.g. Scheduling's "Confirm & Send Agreement" path) and only wants
   * this panel for the status display.
   */
  hideAction?: boolean;
  /**
   * Layout variant. `inline` is the compact one used inside the appointment
   * detail modal where the CTA lives in the modal footer. `card` adds a
   * surrounding rounded panel for standalone surfaces like the inspection
   * page header.
   */
  variant?: 'inline' | 'card';
}

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

export const AgreementSendPanel: React.FC<AgreementSendPanelProps> = ({
  appointmentId,
  clientEmail,
  agreement,
  onSent,
  hideAction = false,
  variant = 'inline',
}) => {
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justSent, setJustSent] = useState(false);

  const canSend =
    !hideAction &&
    !!clientEmail &&
    (!agreement || agreement.status === 'expired');

  const handleSend = async () => {
    setSending(true);
    setError(null);
    setJustSent(false);
    try {
      const res = await agreementsApi.createForAppointment(appointmentId);
      if (res.success && res.data) {
        const { agreement: sent, emailSent, emailError } = res.data;
        onSent(sent);
        if (!emailSent) {
          setError(`Saved, but email could not be delivered: ${emailError ?? 'unknown error'}`);
        } else {
          setJustSent(true);
        }
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const msg =
        axiosErr?.response?.data?.error ??
        'Failed to send agreement. Ensure you have an agreement template in Settings → Agreement.';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const content = (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {agreement?.status === 'signed' ? (
          <>
            <Badge tone="success">Signed ✓</Badge>
            {agreement.signedAt && (
              <span className="text-body-sm text-ink-subtle">
                {formatDate(agreement.signedAt)}
              </span>
            )}
            {agreement.signedPdfUrl && (
              <a
                href={agreement.signedPdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-body-sm text-brand hover:underline"
              >
                View PDF
              </a>
            )}
          </>
        ) : agreement?.status === 'pending' ? (
          <>
            <Badge tone="info">Awaiting signature</Badge>
            {agreement.sentAt && (
              <span className="text-body-sm text-ink-subtle">
                Sent {formatDate(agreement.sentAt)}
              </span>
            )}
          </>
        ) : agreement?.status === 'expired' ? (
          <>
            <Badge tone="warning">⚠ Expired</Badge>
            <span className="text-body-sm text-ink-subtle">
              Link expired — resend to get a new link
            </span>
          </>
        ) : (
          <>
            <Badge tone="neutral" dashed>Unsent</Badge>
            <span className="text-body-sm text-ink-subtle">
              {clientEmail ? 'Not yet sent.' : 'Add a client email to send an agreement.'}
            </span>
          </>
        )}
        {canSend && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            loading={sending}
            className="ml-auto"
          >
            {agreement?.status === 'expired' ? 'Resend agreement' : 'Send agreement'}
          </Button>
        )}
      </div>

      {justSent && clientEmail && (
        <div className="mt-2 p-2 bg-sev-safe-soft text-sev-safe-ink border border-sev-safe/20 rounded-md text-body-sm">
          Agreement sent to {clientEmail}.
        </div>
      )}
      {error && (
        <div className="mt-2 p-2 bg-sev-minor-soft text-sev-minor-ink border border-sev-minor/20 rounded-md text-body-sm">
          {error}
        </div>
      )}
    </>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-xl border border-border-subtle bg-surface-1 p-4">
        <p className="text-body-xs text-ink-subtle font-medium uppercase tracking-wide mb-2">
          Pre-inspection agreement
        </p>
        {content}
      </div>
    );
  }

  return <div>{content}</div>;
};

export default AgreementSendPanel;
